import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { useRef } from 'react'
import { useSelector } from 'react-redux'
import Dropdown, {
  DropdownInfiniteScrollProps,
  DropdownItemProps,
  DropdownOption,
  DropdownOptionGroup,
  MultiItemPillProps,
  MultiItemProps,
} from 'src/components/Dropdown/Dropdown'
import EmptyDropdownMessage from 'src/components/EmptyDropdownMessage/EmptyDropdownMessage'
import TextAvatar from 'src/components/TextAvatar/TextAvatar'
import UserAvatar from 'src/components/UserAvatar/UserAvatar'
import { AvatarSizes, svgImageNames } from 'src/enums'
import { useColorOrGrayscaleSVG } from 'src/hooks/brandHooks'
import { useTranslate } from 'src/localization/Trns'
import { UserListModel } from 'src/models/user.models'
import {
  selectUserIdFromState,
  selectUserListModelFromState,
} from 'src/redux/selectors/auth.selectors'
import { selectUserExplicitOrganizationListModelFromState } from 'src/redux/selectors/data/organizations.selectors'
import { selectGuestUserEmailMap } from 'src/redux/selectors/data/users.selectors'
import styled, { css, CSSProp } from 'styled-components'

export interface UserOptionDetails {
  user: UserListModel
  hideOrgRolePills?: boolean
}

export type UserOption = DropdownOption<UserOptionDetails>
export const userEmailOption = (user: UserListModel): UserOption => ({
  value: user.email,
  label: user.fullNameOrEmail,
  details: {
    user: user,
  },
})
export const userIdOption = (user: UserListModel): UserOption => ({
  value: user.id,
  label: user.fullNameOrEmail,
  details: {
    user: user,
  },
})

const StyledDiv = styled.div<{ isRoot: boolean }>`
  display: flex;
  align-items: center;
  flex-grow: 2;
  height: 60px;

  ${({ isRoot }): CSSProp => {
    return isRoot
      ? css`
          height: 100%;
        `
      : css``
  }}

  min-width: 0;
  overflow: hidden;
`

const UserDropdownItem: React.FC<DropdownItemProps<UserOptionDetails>> = (
  props,
): React.ReactElement => {
  const translate = useTranslate()

  const userId = useSelector(selectUserIdFromState)

  return (
    <StyledDiv className={props.className} isRoot={props.isRoot}>
      {props.minimalDisplay ? (
        props.details?.user.fullNameOrEmail || props.value
      ) : (
        <>
          {props.details !== undefined ? (
            <UserAvatar user={props.details.user} size={AvatarSizes.Medium} />
          ) : (
            <TextAvatar text={props.value[0]} size={AvatarSizes.Medium} />
          )}
          <div
            css={css`
              display: flex;
              flex-direction: column;
              margin-left: 10px;
              overflow: hidden;
            `}
          >
            {props.details?.user.hasFullName && (
              <div
                css={css`
                  display: flex;
                `}
              >
                <span
                  css={css`
                    ${getFontCss(
                      16,
                      themeConstants.colors.font_primary,
                      styleConstants.fontFamilies.medium,
                    )}
                    margin-bottom: 2px;
                    margin-right: 6px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;

                    ${props.isSelected &&
                    getFontCss(
                      16,
                      themeConstants.colors.cta,
                      styleConstants.fontFamilies.medium,
                    )}
                  `}
                >
                  {props.details.user.truncatedFullNameOrEmail}
                </span>
                {props.details.user.id === userId && (
                  <span
                    css={css`
                      margin-right: 6px;
                    `}
                  >
                    {translate('(You)')}
                  </span>
                )}
              </div>
            )}
            <div
              css={css`
                display: flex;
                gap: 8px;
                align-items: center;
              `}
            >
              <span
                id={props.details?.user.email || props.value}
                css={css`
                  overflow: hidden;
                  text-overflow: ellipsis;
                  ${getFontCss(
                    12,
                    styleConstants.colors.gray_darkest,
                    styleConstants.fontFamilies.regular,
                  )}
                `}
              >
                {props.details?.user.email || props.value}
              </span>
            </div>
          </div>
        </>
      )}
    </StyledDiv>
  )
}

const UserMultiItem: React.FC<MultiItemProps<UserOptionDetails>> = (
  props,
): React.ReactElement => {
  const translate = useTranslate()

  return (
    <StyledDiv className={props.className} isRoot={true}>
      {props.options.length === 1 &&
        (props.options[0].details?.user.fullNameOrEmail ||
          props.options[0].value)}
      {props.options.length > 1 &&
        translate('${selectedOption}, +${count} more', {
          selectedOption:
            props.options[0].details?.user.fullNameOrEmail ||
            props.options[0].value,
          count: `${props.options.length - 1}`,
        })}
    </StyledDiv>
  )
}

const UserMultiItemPills: React.FC<MultiItemPillProps<UserOptionDetails>> = (
  props,
): React.ReactElement => {
  const inputRef: React.MutableRefObject<HTMLInputElement | null> = useRef(null)

  return (
    <>
      {props.options.map((option, index) => (
        <div
          key={index}
          ref={inputRef}
          data-testid="user-multi-item-pill"
          {...(option.details?.user.firstName &&
            option.details?.user.lastName && {
              'data-test-name': `${option.details.user.firstName} ${option.details.user.lastName}`,
              'data-test-email': `${option.details?.user.email}`,
              'data-test-userid': `${option.details?.user.id}`,
            })}
          css={css`
            padding: 2px 6px;
            ${getFontCss(
              14,
              themeConstants.colors.cta,
              styleConstants.fontFamilies.medium,
            )}
            cursor: pointer;
            border-radius: 4px;
            margin-right: 4px;

            ${props.selectedIndex === index &&
            css`
              background-color: ${themeConstants.colors.cta};
              ${getFontCss(
                14,
                styleConstants.colors.white,
                styleConstants.fontFamilies.medium,
              )}
            `}
          `}
          onClick={(): void => {
            props.selectedIndex !== index
              ? props.setSelectedIndex(index)
              : props.setSelectedIndex(undefined)
          }}
          onKeyPress={(): void => {
            props.selectedIndex !== index
              ? props.setSelectedIndex(index)
              : props.setSelectedIndex(undefined)
          }}
          role={'button'}
          tabIndex={0}
        >
          {index + 1 === props.options.length
            ? `${option.details?.user.cardImageFullName || option.details?.user.email || option.value}`
            : `${option.details?.user.cardImageFullName || option.details?.user.email || option.value},`}
        </div>
      ))}
    </>
  )
}

const UserDropdownNoneItem: React.FC = (): React.ReactElement => {
  const translate = useTranslate()

  return (
    <div
      css={css`
        display: flex;
        align-items: center;
        ${getFontCss(
          16,
          styleConstants.colors.black,
          styleConstants.fontFamilies.regular,
        )};
        height: 60px;
        opacity: 0.5;
      `}
    >
      {translate('(none)')}
    </div>
  )
}

const getUserOptionFilter = (option: UserOption, val: string): boolean =>
  !!option.details?.user.email
    .toLowerCase()
    .includes(val.toLowerCase().trim()) ||
  !!option.details?.user.firstNameOrEmail
    .toLowerCase()
    .includes(val.toLowerCase().trim()) ||
  option.value === val

const getUserOptionFilterText = (option: UserOption): string =>
  option.details !== undefined
    ? option.details.user.email.trim()
    : option.value.trim()

export type UserDropdownInfiniteScrollProps = DropdownInfiniteScrollProps
export interface UserDropdownProps {
  className?: string
  optionGroups: Array<DropdownOptionGroup<UserOption>>
  recentOptionGroups?: Array<DropdownOptionGroup<UserOption>>
  selectedValues: Set<string>
  chooseOption: (val: string) => void
  chooseMultiOption?: (val: string[]) => void
  placeholder?: string
  fieldLabel?: React.ReactNode
  errors?: Array<React.ReactElement | string>
  newOptionLabel?: React.ReactNode
  isStatic?: boolean
  isMulti?: boolean
  showSuggested?: boolean
  isCreatable: boolean
  externalReset?: {
    resetClicked: boolean
    setResetClicked: (value: boolean) => void
  }
  onResetClick?: () => void
  infiniteScrollProps?: UserDropdownInfiniteScrollProps
  selectedOptions?: UserOption[]
  tooltip?: React.ReactElement
  displayPills?: boolean
  setShowMessageCard?: (value: boolean) => void
  id?: string
  allowNone?: boolean
  minimalDisplay?: boolean
  inputDisabled?: boolean
  showOutsideOrgAlert?: React.ReactNode
  errorBorder?: boolean
  preventGuests?: boolean
  'data-testid'?: string
  startAndKeepOpen?: boolean
  embeddedStyle?: boolean
}

const UserDropdown: React.FC<UserDropdownProps> = (
  props,
): React.ReactElement => {
  const translate = useTranslate()

  const user = useSelector(selectUserListModelFromState)
  const userOrganization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )
  const guestUserEmails = useSelector(selectGuestUserEmailMap)
  const noUsersImageSvg = useColorOrGrayscaleSVG(svgImageNames.NoUsers)
  const validateNewOption = (val: string): boolean => {
    // validate that new contact is an org member
    const newOptionIsMember =
      props.optionGroups?.length > 0 &&
      props.optionGroups[0].options.filter(
        (option) => option.details?.user.email === val,
      ).length > 0

    const newOptionIsGuest = guestUserEmails.indexOf(val) > -1

    const isSelectingMember =
      (!user?.hasOrgBookkeeperPermissions &&
        !userOrganization?.memberListVisible) ||
      newOptionIsMember
    const isCreatingNew =
      props.isCreatable &&
      !!val.match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
      )
    return (
      (isSelectingMember || isCreatingNew) &&
      !(props.preventGuests && newOptionIsGuest)
    )
  }

  return (
    <Dropdown
      className={props.className}
      fieldLabel={props.fieldLabel}
      DropdownItem={UserDropdownItem}
      MultiItem={UserMultiItem}
      MultiItemPills={UserMultiItemPills}
      optionGroups={
        user?.hasOrgBookkeeperPermissions || userOrganization?.memberListVisible
          ? props.optionGroups
          : []
      }
      recentOptionGroups={props.recentOptionGroups}
      isMulti={!!props.isMulti}
      allowNoneProps={
        props.allowNone
          ? {
              NoneDropdownItem: UserDropdownNoneItem,
              deselectAllOptions: (): void => props.chooseOption(''),
            }
          : undefined
      }
      showSuggested={!!props.showSuggested}
      selectedValues={props.selectedValues}
      chooseOption={props.chooseOption}
      chooseMultiOption={props.chooseMultiOption}
      placeholder={props.placeholder}
      errors={props.errors}
      filter={getUserOptionFilter}
      getFilterText={getUserOptionFilterText}
      newOptionLabel={props.newOptionLabel}
      validateNewOption={validateNewOption}
      customEmptyDropdownMessage={
        props.isCreatable ? (
          <EmptyDropdownMessage
            imageSvg={noUsersImageSvg}
            subtitle={translate(
              `If you're looking to add a new contact, please provide a full email address.`,
            )}
          />
        ) : undefined
      }
      isStatic={props.isStatic}
      selectedOptions={props.selectedOptions}
      externalReset={props.externalReset}
      onResetClick={props.onResetClick}
      infiniteScrollProps={props.infiniteScrollProps}
      tooltip={props.tooltip}
      displayPills={props.displayPills}
      setShowMessageCard={props.setShowMessageCard}
      id={props.id}
      data-testid={props['data-testid']}
      minimalDisplay={props.minimalDisplay}
      inputDisabled={props.inputDisabled}
      showOutsideOrgAlert={props.showOutsideOrgAlert}
      errorBorder={props.errorBorder}
      startAndKeepOpen={props.startAndKeepOpen}
      embeddedStyle={props.embeddedStyle}
    />
  )
}

export default UserDropdown
